import { CommandBar, DetailsList, Panel, PanelType, Spinner, Selection, SelectionMode, MessageBar, MessageBarType, ActionButton, IColumn, IconButton, TooltipHost, IDetailsColumnProps, Dialog } from "office-ui-fabric-react"
import { useEffect, useState } from "react";
import { Route, RouteComponentProps, useHistory } from "react-router";
import { deleteRoom, loadRoomsByLocationInclDisabled } from "../../../api/rooms";
import { FloorplansPanel } from "./FloorplansPanel";
import { RoomFormPanel } from "../forms/RoomFormPanel";
import { IRoom } from "../../../types/SiteLocation.types"
import RoomQRCodeDialog from "./RoomQRCodeDialog";

export interface IRoomsPanelProps {
    onDismiss: () => void;
    siteId: number;
    locationId: number;
}

export const RoomsPanel = (props: IRoomsPanelProps) => {
    const { siteId, locationId, onDismiss } = props;
    const history = useHistory();

    const [ rooms, setRooms ] = useState<Partial<IRoom>[]>();
    const [ error, setError ] = useState<string>();
    const [ loading, setLoading ] = useState(false);
    const [ locationSelected, setLocationSelected ] = useState(false);
    const [ showQR, setShowQR ] = useState<Partial<IRoom>>();

    const onSelectionChanged = () => {
        const selectedItems = selection.getSelection() as IRoom[];
        if (selectedItems !== undefined && selectedItems.length > 0) {
            setLocationSelected(true);
        } else {
            setLocationSelected(false);
        }
    };
    
    const [ selection ] = useState(new Selection({ onSelectionChanged }));

    const _loadRooms = async () => {
        setLoading(true);
        selection.setAllSelected(false);
        setLocationSelected(false);
        const locationsResponse = await loadRoomsByLocationInclDisabled(props.locationId);
        if (locationsResponse.ok) {
            setRooms(await locationsResponse.json());
        } else {
            setError("Unable to load rooms: " + locationsResponse.statusText);
        }
        setLoading(false);
    };

    const _deleteRoom = async (id: number) => {
        const deleteResponse = await deleteRoom(id);
        if (deleteResponse.ok) {
            _loadRooms();
        } else {
            setError("Unable to delete room: " + deleteResponse.statusText)
        }
    }

    useEffect(() => {
        _loadRooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Panel 
            headerText="Rooms" 
            isOpen={true} 
            onDismiss={onDismiss}
            type={PanelType.medium} 
        >
            { error !== undefined && (
                <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
            )}
            <p><strong>Create or edit rooms</strong></p>
            <p>
                Using the interface below - you can create, edit and enable/disable meeting rooms
            </p>
            <CommandBar
                items={[
                    {
                        key: "new",
                        text: "New",
                        iconProps: { iconName: "Add" },
                        onClick: () => history.push(`/menu/settings/sites/${siteId}/locations/${locationId}/rooms/new`)
                    },
                    {
                        disabled: !locationSelected,
                        key: "edit",
                        text: "Edit",
                        iconProps: { iconName: "Edit" },
                        onClick: () => {
                            const selectedItems = selection.getSelection() as IRoom[];
                            if (selectedItems !== undefined && selectedItems.length > 0) {
                                const selectedItem = selectedItems[0];
                                history.push(`/menu/settings/sites/${siteId}/locations/${locationId}/rooms/${selectedItem.Id}/edit`);
                            }
                        }
                    },
                    {
                        disabled: !locationSelected,
                        key: "delete",
                        text: "Delete",
                        iconProps: { iconName: "Delete" },
                        onClick: () => {
                            const selectedItems = selection.getSelection() as IRoom[];
                            if (selectedItems !== undefined && selectedItems.length > 0) {
                                const selectedItem = selectedItems[0];
                                if (window.confirm("Are you sure? Note: Please make sure there are no bookings active for this room before deleting.")) {
                                    _deleteRoom(selectedItem.Id);
                                }
                            }
                        }
                    }
                ]}
            />
            { loading === true && (
                <Spinner />
            )}
            { loading === false && rooms !== undefined && rooms.length > 0 && (
                <DetailsList
                    items={rooms.sort((a,b) => (a?.Name || "") < (b?.Name || "") ? -1 : 1)}
                    columns={[
                        {
                            key: "name",
                            fieldName: "Name",
                            name: "Name",
                            minWidth: 40
                        },
                        {
                            key: "qrCode",
                            fieldName: "ID",
                            name: "QR",
                            onRenderHeader: (colProps, defaultRenderer): JSX.Element => {
                                return (
                                    <TooltipHost content="QR Codes">
                                        {defaultRenderer ? defaultRenderer(colProps) : <span>{colProps?.column.name}</span>}
                                    </TooltipHost>
                                )
                            },
                            minWidth: 40,
                            maxWidth: 40,
                            onRender: (item: IRoom, index?: number, column?: IColumn) => {
                                return (
                                    <TooltipHost content="Get QR Code">
                                        <ActionButton iconProps={{iconName: "QRCode"}} onClick={() => {
                                            setShowQR(item);
                                        }} />
                                    </TooltipHost>
                                )
                            }
                        },
                        {
                            key: "disabled",
                            fieldName: "Disabled",
                            name: "Disabled",
                            isIconOnly: true,
                            iconName: "AlertSettings",
                            minWidth: 40,
                            onRender: (item: IRoom, index?: number, column?: IColumn) => {
                                if (!item.Disabled) return null;
                                return (
                                    <TooltipHost content="This room has been disabled. Click here to edit the room.">
                                        <IconButton iconProps={{iconName: "AlertSettings"}} onClick={() => {
                                            history.push(`/menu/settings/sites/${item.SiteId}/locations/${item.LocationId}/rooms/${item.Id}/edit`);
                                        }} />
                                    </TooltipHost>
                                )
                            }
                        }
                    ]}
                    selection={selection}
                    selectionMode={SelectionMode.single}
                />
            )}
            { showQR !== undefined && (
                <RoomQRCodeDialog room={showQR} onDismiss={() => setShowQR(undefined)} />
            )}
            <Route path="/menu/settings/sites/:siteId/locations/:locationId/rooms/new" exact={true} component={(route: RouteComponentProps<{siteId?: string|undefined, locationId?: string|undefined}>) => {
                if (!Number.isNaN(Number(route.match.params.siteId) && !Number.isNaN(Number(route.match.params.locationId)))) {
                    return <RoomFormPanel room={{ SiteId: Number(route.match.params.siteId), LocationId: Number(route.match.params.locationId) }} onDismiss={(reloadLocations) => {
                        history.goBack();
                        if (reloadLocations) {
                            _loadRooms();
                        }
                    }} />;
                }
                return null;
            }} />
            <Route path="/menu/settings/sites/:siteId/locations/:locationId/rooms/:roomId/edit" exact={true} component={(route: RouteComponentProps<{roomId?: string|undefined}>) => {
                if (!Number.isNaN(Number(route.match.params.roomId))) {
                    const matchingRoom = rooms?.filter((l) => l.Id === Number(route.match.params.roomId))[0];
                    if (matchingRoom) {
                        return <RoomFormPanel room={matchingRoom} onDismiss={(reloadLocations) => {
                            history.goBack();
                            if (reloadLocations) {
                                _loadRooms();
                            }
                        }} />
                    }
                }
                return null;
            }} />
        </Panel>
    )
}